import React, { forwardRef } from "react";
import * as arrow from "apache-arrow";
import { useTheme } from "@mui/material/styles";
import { TableVirtuoso, TableVirtuosoProps } from "react-virtuoso";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  getFormattedCell,
  getFormattedHeader,
  getFirstSegmentationColumnIndex,
  getFormattedTotalCell,
} from "../../utils/mui_table_utils";

export interface MUIFlatTableColumn {
  key: string;
  label?: string;
  sticky?: boolean;
  width?: number | string;
  align?: "left" | "center" | "right";
  renderHeader?: () => React.ReactNode;
  renderCell?: (row: any, rowIndex: number) => React.ReactNode;
  onSort?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  sortDirection?: "asc" | "desc" | undefined;
  sortable?: boolean;
  cellType?: arrow.DataType;
  headerType?: arrow.DataType;
  // Overrides col.key when passed to formatCellValue — used by pivot date
  // columns to carry the active measure name (e.g. "cost") so currency
  // formatting applies even though the column key is a date timestamp.
  currencyKey?: string;
  // Expand/collapse support
  isExpandColumn?: boolean;
}

// Enhanced row interface to support expand/collapse (all properties optional for backward compatibility)
export interface MUIFlatTableRow {
  type?: "group" | "detail";
  groupKey?: string;
  isExpanded?: boolean;
  groupSize?: number;
  [key: string]: any;
}

export interface MUIFlatTableProps {
  columns: MUIFlatTableColumn[];
  getRow: (rowIndex: number) => MUIFlatTableRow;
  rowCount: number;
  stickyHeader?: boolean;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  sortConfig?: { key: string; direction: "asc" | "desc" }[];
  shadeHistory?: boolean;
  showHeaderRow?: boolean;
  columnTotals?:
    | Record<string, number>
    | Array<{ tableId: string; totals: Record<string, number> }>; // Support both structures
  // Expand/collapse support
  expandedGroups?: Set<string>;
  onToggleGroup?: (groupKey: string) => void;
  // Header expansion support
  isHeaderExpanded?: boolean;
  onToggleHeader?: () => void;
}

const TableComponents: TableVirtuosoProps<any, unknown>["components"] = {
  Scroller: forwardRef<HTMLDivElement, any>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table
      {...props}
      sx={{
        borderCollapse: "separate",
      }}
    />
  ),
  TableHead: forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableHead component="thead" ref={ref} {...props} />
  )),
  TableRow: TableRow,
  TableBody: forwardRef<HTMLTableSectionElement, any>((props, ref) => (
    <TableBody component="tbody" ref={ref} {...props} />
  )),
};

export const MUIFlatTable: React.FC<MUIFlatTableProps> = ({
  columns,
  getRow,
  rowCount,
  stickyHeader = true,
  columnHeaderMap,
  columnFormatters,
  sortConfig,
  shadeHistory = false,
  showHeaderRow = false,
  columnTotals,
  // Expand/collapse support
  expandedGroups,
  onToggleGroup,
  // Header expansion support
  isHeaderExpanded = false,
  onToggleHeader,
}) => {
  const theme = useTheme();

  const firstSegmentationIndex = getFirstSegmentationColumnIndex(
    columns,
    getRow,
    rowCount
  );

  // Calculate left offset for sticky columns
  const getStickyLeftOffset = (columnIndex: number) => {
    let offset = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i].sticky) {
        offset += Number(columns[i].width) || 0;
      }
    }
    return offset;
  };

  return (
    <TableVirtuoso
      key={sortConfig ? JSON.stringify(sortConfig) : "no-sort"}
      totalCount={rowCount}
      components={TableComponents}
      fixedHeaderContent={() => (
        <>
          {/* Column headers */}
          <TableRow>
            {columns.map((col, index) => {
              const isSortable = col.sortable && col.onSort;
              // For sortable columns, always use getFormattedHeader to show sort arrows
              // For non-sortable columns, use label if available, otherwise use getFormattedHeader
              const headerContent = col.isExpandColumn
                ? "" // Empty string for expand columns
                : col.renderHeader
                  ? col.renderHeader()
                  : isSortable
                    ? getFormattedHeader(
                        columnHeaderMap ?? {},
                        col.headerType ?? new arrow.Utf8(),
                        sortConfig?.find((s) => s.key === col.key)
                          ? {
                              ...sortConfig.find((s) => s.key === col.key)!,
                              index: sortConfig.findIndex(
                                (s) => s.key === col.key
                              ),
                            }
                          : undefined,
                        col.key
                      )
                    : col.label ||
                      getFormattedHeader(
                        columnHeaderMap ?? {},
                        col.headerType ?? new arrow.Utf8(),
                        sortConfig?.find((s) => s.key === col.key)
                          ? {
                              ...sortConfig.find((s) => s.key === col.key)!,
                              index: sortConfig.findIndex(
                                (s) => s.key === col.key
                              ),
                            }
                          : undefined,
                        col.key
                      );
              return (
                <Tooltip
                  key={col.key}
                  title={
                    isSortable
                      ? "Click to sort or Shift+Click for multi line sort"
                      : ""
                  }
                  placement="bottom-start"
                  disableHoverListener={!isSortable}
                  disableFocusListener={!isSortable}
                  disableTouchListener={!isSortable}
                  slotProps={{
                    tooltip: {
                      sx: {
                        marginTop: "-4px !important",
                        transform: "translateY(-4px) !important",
                      },
                    },
                  }}
                >
                  <TableCell
                    align={col.align || "left"}
                    sx={{
                      backgroundColor: "white",
                      cursor: isSortable ? "pointer" : "default",
                      height: "48px", // Slightly taller for headers
                      verticalAlign: "middle",
                      fontWeight: "bold",
                      width: col.width,
                      minWidth: col.width,
                      ...(col.sticky && {
                        position: "sticky",
                        top: 0,
                        left: getStickyLeftOffset(index),
                        backgroundColor: "white",
                      }),
                    }}
                    onClick={isSortable ? col.onSort : undefined}
                  >
                    {headerContent}
                  </TableCell>
                </Tooltip>
              );
            })}
          </TableRow>

          {/* Summary row with totals */}
          {showHeaderRow &&
            (() => {
              const hasTableId = columns.some((col) => col.key === "table_id");
              const hasExpandColumn = columns.some((col) => col.isExpandColumn);

              // Helper function to render header cells
              const renderHeaderCell = (
                col: MUIFlatTableColumn,
                index: number,
                rowData: Record<string, any>,
                backgroundColor: string,
                fontWeight: string = "normal",
                showExpandButton: boolean = false
              ) => {
                if (col.isExpandColumn) {
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        p: 0,
                        height: "40px",
                        verticalAlign: "middle",
                        width: col.width,
                        minWidth: col.width,
                        backgroundColor,
                        ...(col.sticky && {
                          position: "sticky",
                          backgroundColor,
                          left: getStickyLeftOffset(index),
                        }),
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0,
                        }}
                      >
                        {showExpandButton && onToggleHeader && (
                          <IconButton
                            size="small"
                            onClick={onToggleHeader}
                            sx={{ p: 0 }}
                          >
                            {isHeaderExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  );
                }

                const cellContent = col.renderCell
                  ? col.renderCell(rowData, -1)
                  : getFormattedCell({
                      value: rowData[col.key],
                      col: col.currencyKey ?? col.key,
                      columnFormatters,
                      row: rowData,
                      isDelta: rowData.table_id === "delta (1st - 2nd)",
                      theme,
                      columnType: col.cellType,
                      headerType: col.headerType,
                      shadeHistory,
                      align: col.align,
                    });

                return (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{
                      p: 0,
                      height: "40px",
                      verticalAlign: "middle",
                      width: col.width,
                      minWidth: col.width,
                      backgroundColor,
                      fontWeight,
                      ...(col.sticky && {
                        position: "sticky",
                        backgroundColor,
                        left: getStickyLeftOffset(index),
                      }),
                    }}
                  >
                    {cellContent}
                  </TableCell>
                );
              };

              // For comparison tables with expand/collapse, create expandable header rows
              if (hasTableId && hasExpandColumn) {
                const deltaTableId = "delta (1st - 2nd)";
                const deltaHeaderRow: Record<string, any> = {
                  type: "group" as const,
                  groupKey: `header-${deltaTableId}`,
                  isExpanded: false,
                  groupSize: 2,
                  table_id: deltaTableId,
                  channel: "Total",
                  "product category": "Total",
                  ...(Array.isArray(columnTotals) &&
                    columnTotals.find((t) => t.tableId === deltaTableId)
                      ?.totals),
                };

                return (
                  <>
                    {/* Main delta header row */}
                    <TableRow
                      key={`header-${deltaTableId}`}
                      sx={{
                        backgroundColor: theme.palette.grey[50],
                        borderBottom: "2px solid #ccc",
                      }}
                    >
                      {columns.map((col, index) =>
                        renderHeaderCell(
                          col,
                          index,
                          deltaHeaderRow,
                          theme.palette.grey[50],
                          "bold",
                          true
                        )
                      )}
                    </TableRow>

                    {/* Expanded 1st and 2nd rows */}
                    {isHeaderExpanded &&
                      ["1st", "2nd"].map((tableId) => {
                        const childHeaderRow: Record<string, any> = {
                          type: "detail" as const,
                          groupKey: `header-${deltaTableId}`,
                          table_id: tableId,
                          channel: "Total",
                          "product category": "Total",
                          ...(Array.isArray(columnTotals) &&
                            columnTotals.find((t) => t.tableId === tableId)
                              ?.totals),
                        };

                        return (
                          <TableRow
                            key={`header-child-${tableId}`}
                            sx={{
                              backgroundColor: theme.palette.grey[100],
                              borderBottom: "1px solid #ddd",
                            }}
                          >
                            {columns.map((col, index) =>
                              renderHeaderCell(
                                col,
                                index,
                                childHeaderRow,
                                theme.palette.grey[100]
                              )
                            )}
                          </TableRow>
                        );
                      })}
                  </>
                );
              }

              // For other tables, use the original logic
              const tableIds = hasTableId
                ? ["1st", "2nd", "delta (1st - 2nd)"]
                : [undefined];

              return tableIds.map((tableId) => (
                <TableRow
                  key={tableId ? `total-${tableId}` : "total"}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  {columns.map((col, index) =>
                    getFormattedTotalCell({
                      col,
                      index,
                      firstSegmentationIndex,
                      columnTotals,
                      getRow,
                      rowCount,
                      theme,
                      tableId,
                      isComparison: hasTableId,
                      isSticky: col.sticky,
                      stickyLeftOffset: col.sticky
                        ? getStickyLeftOffset(index)
                        : 0,
                    })
                  )}
                </TableRow>
              ));
            })()}
        </>
      )}
      itemContent={(rowIndex: number) => {
        const row = getRow(rowIndex);
        if (row?.__chunkedLoading) {
          return (
            <>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ color: "text.secondary" }}>
                  Loading…
                </TableCell>
              ))}
            </>
          );
        }
        const isGroup = row.type === "group";

        // Helper function to render expand/collapse button
        const renderExpandButton = (row: MUIFlatTableRow) => {
          if (
            !isGroup ||
            !row.groupSize ||
            row.groupSize <= 1 ||
            !onToggleGroup
          ) {
            return null;
          }

          return (
            <IconButton
              size="small"
              onClick={() => onToggleGroup(row.groupKey!)}
              sx={{ p: 0 }}
            >
              {row.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          );
        };

        // Render detail rows as a single full-width cell in the __details column
        const isDetail = row.type === "detail";
        const detailsColumnIndex = columns.findIndex(
          (c) => c.key === "__details"
        );
        if (isDetail && detailsColumnIndex !== -1) {
          return (
            <>
              {columns.map((col, index) => {
                if (col.isExpandColumn) {
                  return (
                    <TableCell
                      key={col.key}
                      align="center"
                      sx={{
                        p: 0,
                        height: "40px",
                        verticalAlign: "middle",
                        width: col.width,
                        minWidth: col.width,
                        backgroundColor: "white",
                        ...(col.sticky && {
                          position: "sticky",
                          backgroundColor: "white",
                          left: getStickyLeftOffset(index),
                        }),
                      }}
                    />
                  );
                }

                if (col.key === "__details") {
                  const cellContent = col.renderCell
                    ? col.renderCell(row, rowIndex)
                    : null;
                  return (
                    <TableCell
                      key={col.key}
                      colSpan={columns.length - 1}
                      sx={{ p: 0 }}
                    >
                      {cellContent}
                    </TableCell>
                  );
                }
                return null;
              })}
            </>
          );
        }

        return (
          <>
            {columns.map((col, index) => {
              // Handle expand column
              if (col.isExpandColumn) {
                return (
                  <TableCell
                    key={col.key}
                    align="center"
                    sx={{
                      p: 0,
                      height: "40px",
                      verticalAlign: "middle",
                      width: col.width,
                      minWidth: col.width,
                      backgroundColor: "white", // Removed row styling props
                      ...(col.sticky && {
                        position: "sticky",
                        backgroundColor: "white",
                        left: getStickyLeftOffset(index),
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                      }}
                    >
                      {renderExpandButton(row)}
                    </Box>
                  </TableCell>
                );
              }

              // Use renderCell if provided, otherwise use default formatting
              const cellContent = col.renderCell
                ? col.renderCell(row, rowIndex)
                : getFormattedCell({
                    value: row[col.key],
                    col: col.currencyKey ?? col.key,
                    columnFormatters,
                    row,
                    isDelta: row.table_id === "delta (1st - 2nd)",
                    theme,
                    columnType: col.cellType,
                    headerType: col.headerType,
                    shadeHistory,
                    align: col.align,
                  });

              return (
                <TableCell
                  key={col.key}
                  align={col.align || "left"}
                  sx={{
                    p: 0, // Always no padding - let the content handle its own padding
                    height: "40px", // Fixed height for consistent row heights
                    verticalAlign: "middle",
                    width: col.width,
                    minWidth: col.width,
                    ...(col.sticky && {
                      position: "sticky",
                      backgroundColor: "white",
                      left: getStickyLeftOffset(index),
                    }),
                  }}
                >
                  {cellContent}
                </TableCell>
              );
            })}
          </>
        );
      }}
    />
  );
};
